/* @flow */

import * as React from 'react';
import {connect} from 'react-redux';

import {Modal, FooterBar} from 'shells/modal';
import {NutshellPro} from 'shells/illustrations';
import {Button} from 'shells/button';
import {ButtonGroup} from 'shells/button-group';
import {Link} from 'shells/link';
import {BulletedList} from 'shells/bulleted-list';

import {analyticsClient} from '../../analytics-client';
import type {NutshellState} from '../../store';

import {getIsModalOpen, isModalOpen, handlePlanChange} from './';

import './swap-trial-modal.css';

export type Plan = 'lite' | 'pro';

function mapStateToProps(state: NutshellState): StateProps {
    return {
        isSwapTrialModalOpen: getIsModalOpen(state).isModalOpen,
    };
}

const mapDispatchToProps = function(dispatch): DispatchProps {
    return {
        setIsSwapTrialModalOpen: (boolean) => {
            dispatch(isModalOpen(boolean));
        },
        onPlanChange: (string) => {
            dispatch(handlePlanChange(string));
        },
    };
};

type StateProps = {|
    isSwapTrialModalOpen: boolean,
|};

type DispatchProps = {|
    setIsSwapTrialModalOpen: (boolean) => void,
    onPlanChange: (string) => void,
|};

type Props = {
    ...StateProps,
    ...DispatchProps,
};

export function SwapTrialModalComponent(props: Props) {
    const {isSwapTrialModalOpen, setIsSwapTrialModalOpen, onPlanChange} = props;

    React.useEffect(() => {
        if (isSwapTrialModalOpen) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_MODAL_SWAP_VIEW);
        }
    }, [isSwapTrialModalOpen]);

    const currentPlan = NutClientConfig.hasNutshellLiteTrial ? 'lite' : 'pro';

    const headerText =
        currentPlan === 'lite'
            ? 'Upgrade your trial to Nutshell Pro'
            : 'Downgrade your trial to Nutshell';
    const listIntroText =
        currentPlan === 'lite'
            ? 'What’s included in Nutshell Pro:'
            : 'What you’ll be losing from Nutshell Pro:';
    const buttonText = currentPlan === 'lite' ? 'Switch to Pro' : 'Switch to Nutshell';
    const newPlanSku = currentPlan === 'lite' ? 'ns-uub-trial' : 'ns-uud-trial';

    return (
        <Modal
            isOpen={isSwapTrialModalOpen}
            onClose={() => setIsSwapTrialModalOpen(false)}
            headerText={headerText}
            maxHeight={610}
        >
            <div styleName='modal-content'>
                <p styleName='intro'>
                    No commitments here! You can always switch between plans during your trial and
                    we’ll hang onto your work so that you don’t lose anything. Simply select the
                    plan you want when you{' '}
                    <Link
                        href='https://app.nutshell.com/setup/billing'
                        variant='primary'
                        newTab={true}
                    >
                        sign up for an account
                    </Link>
                    .
                </p>
                <p>{listIntroText}</p>
                <div className='flex'>
                    <div styleName='image'>
                        <NutshellPro />
                    </div>
                    <BulletedList
                        size='normal'
                        bullets={[
                            <p key='1'>
                                <strong>Multiple pipelines</strong> to support different sales
                                efforts
                            </p>,
                            <p key='2'>
                                <strong>Personal email sequences</strong> that stop when your
                                prospect replies
                            </p>,
                            <p key='3'>
                                Additional reporting to track the health of your{' '}
                                <strong>sales funnel</strong>
                            </p>,
                            <p key='4'>
                                <strong>Sales and activity quotas</strong> to keep track of your
                                team’s productivity
                            </p>,
                            <p key='5'>
                                <strong>Automated tasks and goals</strong> to help your sellers stay
                                on track
                            </p>,
                            <p key='6'>
                                <strong>Markets</strong> and <strong>territories</strong> that
                                support global sales teams
                            </p>,
                        ]}
                    />
                </div>
                <p>
                    For complete side-by-side comparisons of Nutshell and Nutshell Pro, visit our{' '}
                    <Link href='https://www.nutshell.com/pricing' variant='primary' newTab={true}>
                        pricing page
                    </Link>
                </p>
                <p>
                    Need help deciding?{' '}
                    <Link href='https://www.nutshell.com/support' variant='primary' newTab={true}>
                        Drop us a line
                    </Link>{' '}
                    and we’ll be happy to help
                </p>
                <FooterBar
                    right={
                        <ButtonGroup>
                            <Button
                                variant='text'
                                size='big'
                                onClick={() => setIsSwapTrialModalOpen(false)}
                            >
                                Got it, thanks!
                            </Button>
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => {
                                    onPlanChange(newPlanSku);
                                    window.location.reload();
                                }}
                            >
                                {buttonText}
                            </Button>
                        </ButtonGroup>
                    }
                />
            </div>
        </Modal>
    );
}

const connector = connect<Props, {||}, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const SwapTrialModal = connector(SwapTrialModalComponent);
